/**
 * AddVersionModal — formularz dodawania nowej wersji komponentu cenowego.
 *
 * Wspiera dwa tryby:
 *   - czysta wersja (POST /admin/billing/pricing/components/:id/versions)
 *   - atomic replacement (`closeExistingVersionId` w query) — używane gdy admin
 *     wymienia istniejącą wersję bez okna „bez wersji”.
 *
 * Stan formularza zachowywany w `localStorage` przed POST — jeśli endpoint
 * odpowie 409 / 500, banner z retry pokazuje się a dane nie są tracone.
 *
 * @see AC-11, AC-12 in 2026-05-05-billing-pricing-admin-ui spec
 */
import React, { useEffect, useRef, useState } from "react";
import {
  addPricingComponentVersion,
  listCalculators,
} from "../../../../services/adminProductCatalogApi";
import type {
  CalculatorType,
  CreateComponentVersionDto,
  PricingCalculator,
  PricingComponentVersion,
  PricingMutationConflictReason,
} from "../../../../types/adminBilling";
import { ApplicabilityConstraintForm } from "./ApplicabilityConstraintForm";
import { toIsoFromLocalDate } from "../utils/datetimeUtils";

interface AddVersionModalProps {
  componentId: string;
  brandId: string;
  /** When set, the modal runs an atomic replacement (CB-4). */
  replaceExistingVersion?: {
    id: string;
    version: number;
    validFrom?: string;
    /** Current `validTo` of the version being replaced — used by the inline preview banner. */
    validTo?: string | null;
  };
  onClose: () => void;
  onSuccess: (created: PricingComponentVersion) => void;
  /** Optional refetch callback wired to VERSION_MISMATCH banner ("Odśwież dane"). */
  onRefetch?: () => void;
}

const STORAGE_KEY_PREFIX = "kn:pricing:add-version-draft:";
const STORAGE_TTL_MS = 24 * 60 * 60 * 1000;
/** Upper bound for the calculator dropdown fetch — single brand rarely exceeds dozens. */
const CALCULATOR_FETCH_LIMIT = 100;
/** Soft horizon for `validTo` warning (security LOW-3, review 2026-05-06). */
const VALID_TO_FAR_FUTURE_MS = 5 * 365 * 24 * 60 * 60 * 1000; // ~5 years

/**
 * Soft check: is the supplied date more than 5 years from now?
 * Used to warn admins about typos like 2076 vs 2026 — see security LOW-3
 * (avoids accidentally pushing `WOULD_LEAVE_GAP` into far future).
 */
function isFarFuture(dateString: string): boolean {
  if (!dateString) return false;
  const ts = new Date(dateString).getTime();
  if (!Number.isFinite(ts)) return false;
  return ts > Date.now() + VALID_TO_FAR_FUTURE_MS;
}

interface StoredDraft {
  data: CreateComponentVersionDto;
  timestamp: number;
}

const ALLOWED_INLINE_CALCULATOR_TYPES: ReadonlySet<CalculatorType> = new Set<
  CalculatorType
>([
  "FIXED_AMOUNT",
  "PERCENTAGE",
  "FREE",
  "DISCRETE_POINTS",
  "DEGRESSIVE_EXPONENTIAL",
]);

/**
 * Structural validator for stored drafts (security LOW-2, review 2026-05-06).
 * Hand-rolled type guard rather than a runtime schema lib (the frontend
 * currently has no Zod dependency); enforces the same shape that Zod would:
 * required fields + type-correct values. Malformed entries are removed by
 * the caller.
 */
function isValidStoredDraft(raw: unknown): raw is StoredDraft {
  if (!raw || typeof raw !== "object") return false;
  const obj = raw as Record<string, unknown>;
  if (typeof obj.timestamp !== "number" || !Number.isFinite(obj.timestamp))
    return false;
  if (!obj.data || typeof obj.data !== "object") return false;
  const d = obj.data as Record<string, unknown>;
  if (typeof d.validFrom !== "string") return false;
  if (
    d.validTo !== undefined &&
    d.validTo !== null &&
    typeof d.validTo !== "string"
  )
    return false;
  if (d.calculatorId !== undefined && typeof d.calculatorId !== "string")
    return false;
  if (d.inlineCalculator !== undefined) {
    if (!d.inlineCalculator || typeof d.inlineCalculator !== "object")
      return false;
    const ic = d.inlineCalculator as Record<string, unknown>;
    if (typeof ic.type !== "string") return false;
    if (!ALLOWED_INLINE_CALCULATOR_TYPES.has(ic.type as CalculatorType))
      return false;
    if (!ic.config || typeof ic.config !== "object") return false;
  }
  if (
    d.conditionConfig !== undefined &&
    d.conditionConfig !== null &&
    typeof d.conditionConfig !== "object"
  )
    return false;
  return true;
}

function readDraft(componentId: string): CreateComponentVersionDto | null {
  const key = STORAGE_KEY_PREFIX + componentId;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isValidStoredDraft(parsed)) {
      // Reject + clear malformed drafts (security LOW-2).
      window.localStorage.removeItem(key);
      return null;
    }
    if (Date.now() - parsed.timestamp > STORAGE_TTL_MS) {
      window.localStorage.removeItem(key);
      return null;
    }
    return parsed.data;
  } catch {
    // Parse / JSON error — clear so we don't keep retrying on corrupt data.
    try {
      window.localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
    return null;
  }
}

function saveDraft(componentId: string, data: CreateComponentVersionDto) {
  try {
    window.localStorage.setItem(
      STORAGE_KEY_PREFIX + componentId,
      JSON.stringify({ data, timestamp: Date.now() } satisfies StoredDraft),
    );
  } catch {
    /* quota exceeded — silent */
  }
}

function clearDraft(componentId: string) {
  try {
    window.localStorage.removeItem(STORAGE_KEY_PREFIX + componentId);
  } catch {
    /* ignore */
  }
}

export const AddVersionModal: React.FC<AddVersionModalProps> = ({
  componentId,
  brandId,
  replaceExistingVersion,
  onClose,
  onSuccess,
  onRefetch,
}) => {
  const [calculators, setCalculators] = useState<PricingCalculator[]>([]);
  const [calculatorMode, setCalculatorMode] = useState<"existing" | "inline">(
    "existing",
  );
  const [calculatorId, setCalculatorId] = useState<string>("");
  const [inlineType, setInlineType] = useState<CalculatorType>("FIXED_AMOUNT");
  const [inlineConfigText, setInlineConfigText] = useState<string>("{}");

  const [validFrom, setValidFrom] = useState<string>("");
  const [validTo, setValidTo] = useState<string>("");
  const [conditionConfig, setConditionConfig] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [enableConditionConfig, setEnableConditionConfig] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflictReason, setConflictReason] =
    useState<PricingMutationConflictReason | null>(null);

  // Auto-focus first interactive element on open (a11y)
  const firstFocusRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    firstFocusRef.current?.focus();
  }, []);

  // Escape-to-close (a11y)
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  // Restore draft on mount
  useEffect(() => {
    const draft = readDraft(componentId);
    if (draft) {
      if (draft.calculatorId) {
        setCalculatorMode("existing");
        setCalculatorId(draft.calculatorId);
      } else if (draft.inlineCalculator) {
        setCalculatorMode("inline");
        setInlineType(draft.inlineCalculator.type);
        setInlineConfigText(
          JSON.stringify(draft.inlineCalculator.config, null, 2),
        );
      }
      if (draft.validFrom)
        setValidFrom(draft.validFrom.replace(/[+-]\d\d:\d\d$/, "").slice(0, 16));
      if (draft.validTo)
        setValidTo(draft.validTo.replace(/[+-]\d\d:\d\d$/, "").slice(0, 16));
      if (draft.conditionConfig) {
        setConditionConfig(draft.conditionConfig);
        setEnableConditionConfig(true);
      }
    }
  }, [componentId]);

  useEffect(() => {
    void listCalculators(brandId, { limit: CALCULATOR_FETCH_LIMIT }).then((res) => {
      setCalculators(res.data);
      if (res.data.length > 0 && !calculatorId) {
        setCalculatorId(res.data[0].id);
      }
    });
    // `calculatorId` deliberately excluded from deps: this effect only refetches
    // when the brand changes (calculators are brand-scoped). Including
    // `calculatorId` would re-run on every selection change and clobber the
    // user's choice with `res.data[0].id` after each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId]);

  /**
   * Builds the request DTO from form state, applying client-side validation.
   * Returns `null` and sets `error` when a field is missing/invalid so
   * `handleSubmit` can short-circuit without nesting if/else.
   */
  const buildDto = (): CreateComponentVersionDto | null => {
    if (!validFrom) {
      setError("Pole 'Obowiązuje od' jest wymagane");
      return null;
    }

    // Client-side range validation — avoid roundtrip for obvious INVALID_RANGE.
    if (validTo) {
      const fromMs = new Date(validFrom).getTime();
      const toMs = new Date(validTo).getTime();
      if (Number.isFinite(fromMs) && Number.isFinite(toMs) && toMs <= fromMs) {
        setError(
          "Data 'Obowiązuje do' musi być późniejsza niż 'Obowiązuje od'",
        );
        return null;
      }
    }

    const sharedFields = {
      validFrom: toIsoFromLocalDate(validFrom),
      validTo: validTo ? toIsoFromLocalDate(validTo) : null,
      conditionConfig:
        enableConditionConfig && conditionConfig ? conditionConfig : null,
    };

    if (calculatorMode === "existing") {
      if (!calculatorId) {
        setError("Wybierz kalkulator");
        return null;
      }
      return { calculatorId, ...sharedFields };
    }

    let inlineConfig: Record<string, unknown>;
    try {
      inlineConfig = JSON.parse(inlineConfigText);
    } catch {
      setError("Nieprawidłowy JSON kalkulatora");
      return null;
    }
    return {
      inlineCalculator: { type: inlineType, config: inlineConfig },
      ...sharedFields,
    };
  };

  const handleSubmit = async () => {
    setError(null);
    setConflictReason(null);

    const dto = buildDto();
    if (!dto) return;

    saveDraft(componentId, dto);
    setIsSubmitting(true);
    try {
      const created = await addPricingComponentVersion(
        componentId,
        brandId,
        dto,
        replaceExistingVersion
          ? {
              closeExistingVersionId: replaceExistingVersion.id,
              closeExistingVersion: replaceExistingVersion.version,
            }
          : undefined,
      );
      clearDraft(componentId);
      // Brief success feedback before modal closes (UX MEDIUM batch E task 3).
      setJustSaved(true);
      setTimeout(() => onSuccess(created), 800);
    } catch (e: unknown) {
      const err = e as {
        response?: {
          status: number;
          data?: { reason?: PricingMutationConflictReason; message?: string };
        };
      };
      const status = err.response?.status;
      if (status === 409) {
        setConflictReason(
          err.response?.data?.reason ?? "DUPLICATE_VALIDITY",
        );
        setError(messageFor(err.response?.data?.reason));
      } else {
        setError(err.response?.data?.message ?? "Nieoczekiwany błąd");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-version-modal-title"
        className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
      >
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
          <h3
            id="add-version-modal-title"
            className="text-lg font-semibold text-neutral-900"
          >
            {replaceExistingVersion ? "Wymień wersję" : "Dodaj wersję"}
          </h3>
          <button
            ref={firstFocusRef}
            type="button"
            onClick={onClose}
            aria-label="Zamknij"
            className="text-neutral-400 hover:text-neutral-600"
          >
            ×
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/*
            AC-12 inline preview banner (downgrade from 3-step wizard, 2026-05-06).
            Shows the admin which version will be closed and what `validTo` will
            be set on it so the atomic replacement is not a black box.
            See spec.md AC-12 "Update 2026-05-06".
          */}
          {replaceExistingVersion && (
            <div
              data-testid="replace-preview-banner"
              className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-900"
            >
              <p className="font-medium">
                Zamkniemy wersję #{replaceExistingVersion.version} (id:{" "}
                <span className="font-mono text-xs">
                  {replaceExistingVersion.id.slice(0, 8)}
                </span>
                )
              </p>
              <p className="text-xs mt-1">
                Obecna validTo:{" "}
                <span className="font-mono">
                  {replaceExistingVersion.validTo
                    ? new Date(
                        replaceExistingVersion.validTo,
                      ).toLocaleString("pl-PL")
                    : "∞ (bez końca)"}
                </span>{" "}
                → zostanie ustawiona na:{" "}
                <span className="font-mono">
                  {validFrom
                    ? new Date(validFrom).toLocaleString("pl-PL")
                    : "(uzupełnij validFrom poniżej)"}
                </span>
              </p>
              <p className="text-xs mt-1">
                Operacja atomowa: PATCH validTo poprzedniej + INSERT nowej w
                jednej transakcji.
              </p>
            </div>
          )}

          {conflictReason && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-900">
              <p className="font-medium">
                Konflikt: {messageFor(conflictReason)}
              </p>
              <p className="text-xs mt-1">
                Dane formularza zostały zachowane w przeglądarce — możesz
                spróbować ponownie po skorygowaniu.
              </p>
              {conflictReason === "VERSION_MISMATCH" && onRefetch && (
                <button
                  type="button"
                  onClick={() => {
                    onRefetch();
                    setConflictReason(null);
                    setError(null);
                  }}
                  className="mt-2 text-xs px-3 py-1 bg-amber-600 text-white rounded hover:bg-amber-700"
                >
                  Odśwież dane
                </button>
              )}
            </div>
          )}

          {/* Calculator mode */}
          <div>
            <label className="block text-xs font-medium text-neutral-700 mb-1">
              Kalkulator
            </label>
            <div className="flex gap-2 mb-2">
              <button
                type="button"
                onClick={() => setCalculatorMode("existing")}
                className={`text-xs px-2 py-1 rounded ${
                  calculatorMode === "existing"
                    ? "bg-primary-600 text-white"
                    : "bg-neutral-100 text-neutral-700"
                }`}
              >
                Istniejący
              </button>
              <button
                type="button"
                onClick={() => setCalculatorMode("inline")}
                className={`text-xs px-2 py-1 rounded ${
                  calculatorMode === "inline"
                    ? "bg-primary-600 text-white"
                    : "bg-neutral-100 text-neutral-700"
                }`}
              >
                Stwórz nowy (inline)
              </button>
            </div>

            {calculatorMode === "existing" ? (
              <select
                value={calculatorId}
                onChange={(e) => setCalculatorId(e.target.value)}
                className="w-full text-sm border border-neutral-300 rounded p-1.5"
              >
                {calculators.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.type})
                  </option>
                ))}
              </select>
            ) : (
              <div className="space-y-2">
                <select
                  value={inlineType}
                  onChange={(e) =>
                    setInlineType(e.target.value as CalculatorType)
                  }
                  className="w-full text-sm border border-neutral-300 rounded p-1.5"
                >
                  <option value="FIXED_AMOUNT">FIXED_AMOUNT</option>
                  <option value="PERCENTAGE">PERCENTAGE</option>
                  <option value="FREE">FREE</option>
                  <option value="DISCRETE_POINTS">DISCRETE_POINTS</option>
                  <option value="DEGRESSIVE_EXPONENTIAL">
                    DEGRESSIVE_EXPONENTIAL
                  </option>
                </select>
                <textarea
                  value={inlineConfigText}
                  onChange={(e) => setInlineConfigText(e.target.value)}
                  rows={6}
                  className="w-full text-xs font-mono border border-neutral-300 rounded p-2"
                  placeholder='{"amountCents": 19000}'
                />
              </div>
            )}
          </div>

          {/* Validity */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-neutral-700 mb-1">
                Obowiązuje od *
              </label>
              <input
                type="datetime-local"
                value={validFrom}
                onChange={(e) => setValidFrom(e.target.value)}
                data-testid="add-version-validFrom"
                className="w-full text-sm border border-neutral-300 rounded p-1.5"
              />
              <p className="text-xs text-neutral-500 mt-1">
                Czas polski (Europe/Warsaw)
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-700 mb-1">
                Obowiązuje do (opcjonalne)
              </label>
              <input
                type="datetime-local"
                value={validTo}
                onChange={(e) => setValidTo(e.target.value)}
                className="w-full text-sm border border-neutral-300 rounded p-1.5"
              />
              <p className="text-xs text-neutral-500 mt-1">
                Pusty = bez końca (∞)
              </p>
              {validTo && isFarFuture(validTo) && (
                <small
                  data-testid="add-version-validTo-far-warning"
                  className="block text-xs text-amber-600 mt-1"
                >
                  Uwaga: data ważności jest oddalona o ponad 5 lat. Czy to
                  celowe?
                </small>
              )}
            </div>
          </div>

          {/* conditionConfig */}
          <div>
            <label className="flex items-center gap-2 text-xs font-medium text-neutral-700 mb-1">
              <input
                type="checkbox"
                checked={enableConditionConfig}
                onChange={(e) => {
                  setEnableConditionConfig(e.target.checked);
                  if (e.target.checked && !conditionConfig)
                    setConditionConfig({ type: "alwaysTrue" });
                }}
              />
              Warunek (conditionConfig)
            </label>
            {enableConditionConfig && conditionConfig && (
              <ApplicabilityConstraintForm
                value={conditionConfig}
                onChange={setConditionConfig}
                disabled={isSubmitting}
              />
            )}
          </div>

          {error && !conflictReason && (
            <div className="p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-neutral-200 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-100 rounded"
          >
            Anuluj
          </button>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={isSubmitting || justSaved}
            className="px-4 py-2 text-sm bg-primary-600 text-white hover:bg-primary-700 rounded disabled:opacity-50"
          >
            {justSaved
              ? "Zapisano!"
              : isSubmitting
              ? "Zapisywanie..."
              : replaceExistingVersion
              ? "Wymień"
              : "Dodaj wersję"}
          </button>
        </div>
      </div>
    </div>
  );
};

function messageFor(reason: PricingMutationConflictReason | undefined): string {
  switch (reason) {
    case "DUPLICATE_VALIDITY":
      return "Wersja z identycznym validFrom już istnieje";
    case "VERSION_MISMATCH":
      return "Ktoś inny zmienił dane — odśwież widok i spróbuj ponownie";
    case "MANAGED_BY_SEED":
      return "Wersja jest zarządzana przez seed — DELETE i wymiana zablokowane";
    case "INVALID_RANGE":
      return "validTo musi być większe niż validFrom";
    case "ALREADY_ACTIVE":
      return "Wersja jest już aktywna";
    case "HAS_REFERENCES":
      return "Wersja ma referencje w zamówieniach";
    case "WOULD_LEAVE_GAP":
      return "Usunięcie zostawiłoby lukę w pokryciu";
    default:
      return "Konflikt — operacja nieudana";
  }
}
